import type { IntakeData, UploadedFile } from "../types";

export type IntakeDataMergeResult = {
  data: IntakeData;
  conflictPaths: string[];
};

function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_key, candidate) => {
    if (typeof candidate !== "object" || candidate === null || Array.isArray(candidate)) return candidate;
    return Object.keys(candidate as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((sorted, key) => {
        sorted[key] = (candidate as Record<string, unknown>)[key];
        return sorted;
      }, {});
  });
}

function deepEqual(left: unknown, right: unknown): boolean {
  return stableStringify(left) === stableStringify(right);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// 3-way 병합: 스칼라/객체는 필드 단위, 배열은 하나의 값 단위로 취급한다.
// base와 local, base와 remote가 모두 다르면 local을 유지하고 충돌 경로를 기록한다.
function mergeValue(base: unknown, local: unknown, remote: unknown, path: string, conflicts: string[]): unknown {
  if (deepEqual(local, remote)) return local;
  if (deepEqual(local, base)) return remote;
  if (deepEqual(remote, base)) return local;

  if (isPlainObject(local) && isPlainObject(remote)) {
    const baseRecord = isPlainObject(base) ? base : {};
    const keys = new Set([...Object.keys(local), ...Object.keys(remote)]);
    const merged: Record<string, unknown> = {};
    keys.forEach((key) => {
      merged[key] = mergeValue(baseRecord[key], local[key], remote[key], path ? `${path}.${key}` : key, conflicts);
    });
    return merged;
  }

  conflicts.push(path || "(root)");
  return local;
}

export function mergeIntakeData(base: IntakeData, local: IntakeData, remote: IntakeData): IntakeDataMergeResult {
  const conflicts: string[] = [];
  const data = mergeValue(base, local, remote, "", conflicts) as IntakeData;
  return { data, conflictPaths: conflicts };
}

// 파일은 UUID 단위 집합 연산으로 병합한다.
// - base에 있었는데 local/remote 어느 한쪽에서 사라졌으면 삭제로 간주한다.
// - local/remote 어느 한쪽에만 새로 생겼으면 추가로 간주한다.
// - 파일 메타데이터(이름 변경 등)는 local이 base와 다르면 local, 아니면 remote를 쓴다.
// - 순서는 local 순서를 기준으로 하고 remote 신규 파일을 remote 순서대로 뒤에 붙인다.
export function mergeUploadedFiles(base: UploadedFile[], local: UploadedFile[], remote: UploadedFile[]): UploadedFile[] {
  const baseById = new Map(base.map((file) => [file.id, file]));
  const localById = new Map(local.map((file) => [file.id, file]));
  const remoteById = new Map(remote.map((file) => [file.id, file]));

  // 신규 파일(base에 없음)이거나 양쪽 모두 유지한 파일만 남긴다.
  const keptIds = new Set(
    [...localById.keys(), ...remoteById.keys()].filter(
      (id) => !baseById.has(id) || (localById.has(id) && remoteById.has(id)),
    ),
  );

  const pickRecord = (id: string): UploadedFile => {
    const localFile = localById.get(id);
    const remoteFile = remoteById.get(id);
    const baseFile = baseById.get(id);
    if (localFile && (!baseFile || !deepEqual(localFile, baseFile))) return localFile;
    return remoteFile ?? localFile ?? baseFile!;
  };

  const merged: UploadedFile[] = [];
  local.forEach((file) => {
    if (keptIds.has(file.id)) merged.push(pickRecord(file.id));
  });
  remote.forEach((file) => {
    if (keptIds.has(file.id) && !localById.has(file.id)) merged.push(pickRecord(file.id));
  });
  return merged;
}
